import { EAiLessonTag } from "../aiAgents.types";

// Same limits as the backend's AI_ASSISTANT_LESSON_CONSTANTS.DOMAIN. The server
// enforces them; these only let the form say what is wrong before saving.
export const AI_LESSON_LIMITS = {
  TITLE: { MIN_LENGTH: 3, MAX_LENGTH: 120 },
  SITUATION: { MAX_LENGTH: 500 },
  REPLY: { MAX_LENGTH: 4000 },
  GUIDANCE: { MIN_LENGTH: 10, MAX_LENGTH: 400 },
  TAGS: { MIN: 1, MAX: 6 },
  KEYWORDS: { MAX: 10, MIN_LENGTH: 2, MAX_LENGTH: 40 },
  CORRECTION_NOTE: { MAX_LENGTH: 1000 },
  SEARCH: { MAX_LENGTH: 100 }
} as const;

// Tags that hold in almost every conversation: a lesson needs at least one
// tag outside this list, or it would apply nearly everywhere.
export const AI_LESSON_NON_DISCRIMINATING_TAGS: EAiLessonTag[] = [
  EAiLessonTag.ConversationOngoing,
  EAiLessonTag.LangFr,
  EAiLessonTag.LangEn,
  EAiLessonTag.LastAiWasQuestion
];

// Display order of the tag chips (the backend enum order).
export const AI_LESSON_TAGS: EAiLessonTag[] = Object.values(EAiLessonTag);

export const AI_LESSON_MESSAGE_TYPES = ["text", "voice", "image"] as const;

// Rule ids the guidance validator reports; each has an
// `aiLesson.guidanceRule.<id>` label.
export const AI_LESSON_GUIDANCE_RULES = [
  "amount",
  "percent",
  "big_number",
  "written_number",
  "free_or_discount",
  "stock_promise",
  "payment_method",
  "url",
  "brackets",
  "no_discriminating_tag"
] as const;

// Backend validation messages, which are also the i18n keys (en = key).
export const AI_LESSON_MESSAGES = {
  TITLE_LENGTH: "The title must be between 3 and 120 characters.",
  SITUATION_LENGTH: "The situation must be 500 characters or less.",
  REPLY_LENGTH: "Each reply must be 4000 characters or less.",
  GUIDANCE_LENGTH: "The guidance must be between 10 and 400 characters.",
  TAGS_COUNT: "Pick between 1 and 6 situations.",
  KEYWORDS: "Up to 10 keywords, of 2 to 40 characters each.",
  NOTE_LENGTH: "The note must be 1000 characters or less.",
  NEEDS_DISCRIMINATING_TAG:
    "Pick at least one situation other than the language or “last reply was a question”."
} as const;

// Lessons tab: one page of the list (the backend accepts up to 50).
export const AI_LESSONS_PAGE_SIZE = 20;

// "Where would it apply?": periods the admin can pick, in days. The backend
// accepts 1 to 90 and defaults to 30.
export const AI_LESSON_FIRE_PREVIEW_DAYS = [7, 30, 90] as const;
export const AI_LESSON_FIRE_PREVIEW_DEFAULT_DAYS = 30;

// Replay before activation. Mirrors the backend's
// AI_ASSISTANT_LESSON_CONSTANTS.REPLAY and the loop's MAX_ROUNDS. The cost per
// round is the owner's own figure; the server computes the real total.
export const AI_LESSON_REPLAY = {
  RUNS: [1, 2, 3],
  DEFAULT_RUNS: 2,
  COST_USD_PER_ROUND: 0.002,
  EXPECTED_ROUNDS_PER_RUN: 2.5,
  MAX_ROUNDS_PER_RUN: 8,
  // An older "running" result reads as interrupted.
  RUNNING_STALE_MS: 600_000,
  // How often an open replay window asks for the result while it runs.
  POLL_MS: 5_000
} as const;

// Codes that have an `aiReplay.*` label; anything else falls back.
export const AI_LESSON_REPLAY_VISUAL_TOOLS = [
  "send_products",
  "send_product_list",
  "send_packs",
  "send_ad_products",
  "send_product_video",
  "send_guide_video",
  "show_more_products",
  "start_pack_builder"
] as const;

export const AI_LESSON_REPLAY_APPROXIMATE_REASONS = [
  "no_snapshot",
  "no_context",
  "no_workflow",
  "media_unread"
] as const;

export const AI_LESSON_REPLAY_ERROR_CODES = [
  "seed_failed",
  "lesson_not_injected",
  "unexpected"
] as const;

export const AI_LESSON_REPLAY_OUTCOMES = [
  "reply",
  "silent",
  "escalated",
  "exhausted",
  "error"
] as const;

export const AI_LESSON_REPLAY_TOOL_MODES = [
  "executed",
  "simulated",
  "terminal"
] as const;
